import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export const SCROLL_STORAGE_PREFIX = 'table_scroll_';
export const SCROLL_CONTAINER_SELECTOR =
  '[data-scroll-container="candidate-profile-table"]';

/**
 * Builds the storage key, leaving out `ignoredSearchParams`. With nothing to
 * ignore, the key is the raw URL, so tables that don't opt in are unaffected.
 */
const getScrollStorageKey = (
  tableId: string,
  locationKey: string,
  ignoredSearchParams: readonly string[]
): string => {
  if (ignoredSearchParams.length === 0) {
    return `${SCROLL_STORAGE_PREFIX}${tableId}_${locationKey}`;
  }

  const [pathname, search = ''] = locationKey.split('?');
  const params = new URLSearchParams(search);
  ignoredSearchParams.forEach((param) => params.delete(param));
  const normalizedSearch = params.toString();

  return `${SCROLL_STORAGE_PREFIX}${tableId}_${pathname}${
    normalizedSearch ? `?${normalizedSearch}` : ''
  }`;
};

/**
 * Hook to restore scroll position when navigating back to a table.
 *
 * ## Problem This Solves
 * When a user clicks on a candidate row in a table, navigates to the candidate
 * detail page, and then returns, they should land at the same scroll position
 * they left from. This provides continuity and allows users to pick up where
 * they left off.
 *
 * ## How It Works (End-to-End Flow)
 *
 * 1. **Saving scroll position** (handled by `saveTableScrollPosition`):
 *    - When user clicks a candidate link, `saveTableScrollPosition` is called
 *    - Stores the current scrollTop value in sessionStorage
 *    - Key format: `table_scroll_{tableId}_{pathname}{search}` (includes filters/pagination)
 *
 * 2. **Restoring scroll position** (handled by this hook):
 *    - When returning to the table page, this hook checks sessionStorage for saved position
 *    - Waits for data to finish loading (via `loading` parameter)
 *    - Uses polling to wait for the DOM to actually have scrollable content
 *    - Scrolls to the saved position once content is ready
 *    - Cleans up the sessionStorage entry after restoration
 *
 * ## Why Polling Instead of a Fixed Timeout
 *
 * Initial implementations used a fixed 750ms delay, but this failed intermittently because:
 * - React state updates (loading = false) don't guarantee DOM has rendered
 * - AG Grid may still be rendering rows after data is available
 * - Network speed variations mean data arrives at unpredictable times
 * - Cache behavior can cause loading states to "flicker" (false -> true -> false)
 *
 * Polling solves this by checking if content is actually scrollable:
 * - `scrollHeight - clientHeight` gives us the maximum scrollable distance
 * - We only scroll when this value >= our target scroll position
 * - This guarantees the rows exist in the DOM before we scroll
 *
 * ## Why hasRestoredRef Exists
 *
 * Prevents multiple scroll restoration attempts within the same page visit:
 * - Set to `true` after successful restoration (or when no restoration needed)
 * - Reset to `false` when the URL changes (user navigates away and back)
 * - Critically, it's set INSIDE the polling callback, not before, so if polling
 *   is cancelled due to loading flickering, we can retry on the next attempt
 *
 * ## Storage Key Structure
 *
 * Key: `table_scroll_{tableId}_{pathname}{search}`
 *
 * Includes search params because scroll position is context-dependent:
 * - Different filters = different result set = different scroll position
 * - Same page with filter A at row 50 is different from filter B at row 50
 * - This ensures we only restore position when returning to the exact same view
 *
 * Params in `ignoredSearchParams` are stripped, for params that can change
 * between leaving and returning without changing the view (e.g. the candidate
 * table's selection `sessionId`, see PAD-4224).
 *
 * @param tableId - Unique identifier for the table (e.g., 'candidate-profile-list')
 * @param loading - Whether data is still loading. Must be `false` before restoration starts.
 *                  Should include ALL loading states: conversation loading, duplicate check, etc.
 * @param ignoredSearchParams - Search params to leave out of the storage key. Must match
 *                  what's passed to `saveTableScrollPosition`. Defaults to none.
 *
 * @example
 * // In your table component:
 * const isTableDataLoading =
 *   isConversationLoading ||
 *   isDuplicateCheckLoading ||
 *   mappedCandidateProfiles.length === 0;
 *
 * useTableScrollRestoration('my-table-id', isTableDataLoading);
 */
export const useTableScrollRestoration = (
  tableId: string,
  loading: boolean,
  ignoredSearchParams: readonly string[] = []
): void => {
  const location = useLocation();

  // Tracks whether we've already restored scroll for this page visit.
  // Prevents duplicate restoration attempts and infinite loops.
  const hasRestoredRef = useRef(false);

  // Full URL path including search params - used to create unique storage keys
  // and to detect when user navigates away/back
  const locationKey = `${location.pathname}${location.search}`;
  const storageKey = getScrollStorageKey(
    tableId,
    locationKey,
    ignoredSearchParams
  );
  // Key format used before PAD-4224 (raw URL, ignored params included). Read as a
  // fallback so positions saved just before the deploy aren't lost.
  const legacyStorageKey = `${SCROLL_STORAGE_PREFIX}${tableId}_${locationKey}`;

  // Main restoration effect - runs when loading state changes
  useEffect(() => {
    // Don't attempt restoration if:
    // - Data is still loading (table content isn't ready)
    // - We've already restored for this page visit
    if (loading || hasRestoredRef.current) {
      return;
    }

    const clearSavedPosition = () => {
      sessionStorage.removeItem(storageKey);
      sessionStorage.removeItem(legacyStorageKey);
    };

    // Check if there's a saved scroll position for this exact URL
    const savedScrollY =
      sessionStorage.getItem(storageKey) ??
      sessionStorage.getItem(legacyStorageKey);
    if (savedScrollY === null) {
      // No saved position - this is a fresh visit, not a "back" navigation
      hasRestoredRef.current = true;
      return;
    }

    const scrollY = parseInt(savedScrollY, 10);
    if (isNaN(scrollY)) {
      // Invalid saved value - clean up and skip restoration
      clearSavedPosition();
      hasRestoredRef.current = true;
      return;
    }

    // --- Polling Logic ---
    // Instead of a fixed timeout, we poll until the DOM content is ready.
    // This handles the race condition where React state says "loaded" but
    // the browser hasn't rendered the table rows yet.

    let attempts = 0;
    const maxAttempts = 30; // 3 seconds max (30 polls * 100ms interval)
    let timeoutId: NodeJS.Timeout;

    const attemptScroll = () => {
      const scrollContainer = document.querySelector(SCROLL_CONTAINER_SELECTOR);

      if (scrollContainer) {
        // Calculate maximum scrollable distance
        // scrollHeight = total content height
        // clientHeight = visible viewport height
        // maxScrollable = how far we CAN scroll (scrollHeight - clientHeight)
        const maxScrollable =
          scrollContainer.scrollHeight - scrollContainer.clientHeight;

        // Content is ready if either:
        // 1. We can scroll to our target position (maxScrollable >= scrollY)
        // 2. We've waited long enough (prevent infinite waiting if content is shorter now)
        if (maxScrollable >= scrollY || attempts >= maxAttempts) {
          // Mark as restored BEFORE scrolling to prevent re-entry
          hasRestoredRef.current = true;

          // Scroll to saved position (capped at max scrollable to handle edge cases
          // where content might be shorter than before, e.g., fewer results)
          scrollContainer.scrollTop = Math.min(scrollY, maxScrollable);

          // Clean up - remove from storage since we've restored
          clearSavedPosition();
          return;
        }
      }

      // Content not ready yet - schedule another attempt
      attempts++;
      if (attempts < maxAttempts) {
        timeoutId = setTimeout(attemptScroll, 100);
      } else {
        // Max attempts reached - scroll to whatever position is available
        // This handles edge cases where content is shorter than expected
        // (e.g., fewer search results than when position was saved)
        hasRestoredRef.current = true;
        const container = document.querySelector(SCROLL_CONTAINER_SELECTOR);
        if (container) {
          const maxScrollable = container.scrollHeight - container.clientHeight;
          container.scrollTop = Math.min(scrollY, Math.max(0, maxScrollable));
        }
        clearSavedPosition();
      }
    };

    // Start polling with a small initial delay to let React finish its render cycle
    timeoutId = setTimeout(attemptScroll, 100);

    // Cleanup function - cancels pending poll if:
    // - Component unmounts
    // - Loading state changes back to true (e.g., user triggered a refresh)
    // - Storage key changes (shouldn't happen without location change)
    //
    // IMPORTANT: We don't set hasRestoredRef here. If cleanup runs because loading
    // flickered (true -> false -> true -> false), we want to retry restoration
    // when loading becomes false again.
    return () => clearTimeout(timeoutId);
  }, [loading, storageKey, legacyStorageKey]);

  // Reset hasRestoredRef when URL changes
  // This allows scroll restoration to work again if user navigates away and back
  useEffect(() => {
    hasRestoredRef.current = false;
  }, [locationKey]);
};

/**
 * Saves the current scroll position before navigating away from a table.
 *
 * Call this in the onClick handler of links that navigate away from the table.
 * The saved position will be restored by `useTableScrollRestoration` when the
 * user navigates back.
 *
 * ## Usage
 *
 * ```tsx
 * const location = useLocation();
 *
 * const handleClick = () => {
 *   saveTableScrollPosition(
 *     'my-table-id',
 *     location.pathname + location.search
 *   );
 * };
 *
 * return <Link to="/detail" onClick={handleClick}>View Details</Link>;
 * ```
 *
 * ## Why locationKey Must Include Search Params
 *
 * The storage key includes the full URL (pathname + search) because scroll
 * position is contextual to the exact view. If the user has filters applied:
 * - `/candidates?skill=React` at scroll 500px
 * - `/candidates?skill=Python` at scroll 200px
 *
 * These are different contexts and need separate scroll positions stored.
 *
 * @param tableId - Same tableId used in `useTableScrollRestoration`
 * @param locationKey - Current URL path + search params (e.g., `location.pathname + location.search`)
 * @param ignoredSearchParams - Same list passed to `useTableScrollRestoration`. Defaults to none.
 */
export const saveTableScrollPosition = (
  tableId: string,
  locationKey: string,
  ignoredSearchParams: readonly string[] = []
): void => {
  const storageKey = getScrollStorageKey(
    tableId,
    locationKey,
    ignoredSearchParams
  );

  // Find the scroll container (the element with the data attribute)
  // Falls back to window scroll if container not found (shouldn't happen in normal use)
  const scrollContainer = document.querySelector(SCROLL_CONTAINER_SELECTOR);
  const scrollY = scrollContainer ? scrollContainer.scrollTop : window.scrollY;

  sessionStorage.setItem(storageKey, String(scrollY));
};
